import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building,
  Tag,
  TrendingUp,
  Clock,
  MessageSquare,
  Edit,
  Loader2,
  Plus,
  Activity,
  User,
  Calendar,
  Star,
} from 'lucide-react';

// ── Types ──

interface Contact {
  _id: string;
  email: string;
  name: string;
  phone?: string;
  company?: string;
  source: 'contact-form' | 'newsletter' | 'order' | 'manual' | 'import' | 'chatbot';
  status: 'lead' | 'prospect' | 'customer' | 'churned';
  tags: string[];
  customFields?: Record<string, unknown>;
  score: number;
  assignedTo?: string;
  totalOrders?: number;
  totalRevenue?: number;
  lastContactedAt?: string;
  lastActivityAt?: string;
  notes?: Array<{ text: string; createdBy?: string; createdAt?: string }>;
  createdAt: string;
  updatedAt: string;
}

interface ActivityItem {
  _id: string;
  type: string;
  description: string;
  metadata?: Record<string, unknown>;
  createdBy?: string;
  createdAt: string;
}

const ACTIVITY_TYPES = [
  'email_sent',
  'email_opened',
  'form_submitted',
  'order_placed',
  'page_viewed',
  'chat_started',
  'note_added',
  'status_changed',
  'call_logged',
  'meeting_scheduled',
  'deal_created',
  'deal_stage_changed',
  'contact_created',
  'contact_merged',
] as const;

type ActivityType = (typeof ACTIVITY_TYPES)[number];

const STATUSES: Contact['status'][] = ['lead', 'prospect', 'customer', 'churned'];

// ── Helpers ──

function statusColor(status: string): string {
  switch (status) {
    case 'lead':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'prospect':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'customer':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'churned':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function activityIcon(type: string) {
  switch (type) {
    case 'email_sent':
    case 'email_opened':
      return <Mail className="h-4 w-4 text-blue-500" />;
    case 'form_submitted':
      return <Edit className="h-4 w-4 text-purple-500" />;
    case 'order_placed':
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    case 'page_viewed':
      return <Activity className="h-4 w-4 text-gray-500" />;
    case 'chat_started':
      return <MessageSquare className="h-4 w-4 text-cyan-500" />;
    case 'note_added':
      return <MessageSquare className="h-4 w-4 text-yellow-500" />;
    case 'status_changed':
      return <Activity className="h-4 w-4 text-orange-500" />;
    case 'call_logged':
      return <Phone className="h-4 w-4 text-green-600" />;
    case 'meeting_scheduled':
      return <Calendar className="h-4 w-4 text-blue-600" />;
    case 'deal_created':
    case 'deal_stage_changed':
      return <TrendingUp className="h-4 w-4 text-purple-600" />;
    case 'contact_created':
    case 'contact_merged':
      return <User className="h-4 w-4 text-gray-600" />;
    default:
      return <Activity className="text-muted-foreground h-4 w-4" />;
  }
}

function formatActivityType(type: string): string {
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatDateTime(date: string | undefined | null): string {
  if (!date) return 'N/A';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

// ── Component ──

export default function CRMContactDetail() {
  const { projectId, contactId } = useParams<{ projectId: string; contactId: string }>();
  const queryClient = useQueryClient();

  // Quick-edit sidebar state
  const [editStatus, setEditStatus] = useState<Contact['status'] | ''>('');
  const [editScore, setEditScore] = useState<number | ''>('');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editTags, setEditTags] = useState('');
  const [quickInfoDirty, setQuickInfoDirty] = useState(false);

  // Notes
  const [newNoteText, setNewNoteText] = useState('');

  // Activity dialog
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState<ActivityType>('call_logged');
  const [activityDescription, setActivityDescription] = useState('');

  // Activity pagination
  const [activityPage, setActivityPage] = useState(1);

  const basePath = `/projects/${projectId}/crm/contacts/${contactId}`;

  // ── Queries ──

  const {
    data: contactRes,
    isLoading: contactLoading,
    isError: contactError,
  } = useQuery({
    queryKey: ['crm-contact', projectId, contactId],
    queryFn: async () => {
      const res = await api.get(basePath);
      return res.data;
    },
    enabled: Boolean(projectId) && Boolean(contactId),
  });

  const contact: Contact | null = contactRes?.data || null;

  // Sync quick-edit fields when contact loads
  const syncedRef = useState<string | null>(null);
  if (contact && syncedRef[0] !== contact._id) {
    syncedRef[1](contact._id);
    setEditStatus(contact.status);
    setEditScore(contact.score);
    setEditAssignedTo(contact.assignedTo || '');
    setEditTags((contact.tags || []).join(', '));
    setQuickInfoDirty(false);
  }

  const { data: activitiesRes, isLoading: activitiesLoading } = useQuery({
    queryKey: ['crm-activities', projectId, contactId, activityPage],
    queryFn: async () => {
      const res = await api.get(`${basePath}/activities`, {
        params: { page: activityPage, limit: 20 },
      });
      return res.data;
    },
    enabled: Boolean(projectId) && Boolean(contactId),
  });

  const activities: ActivityItem[] = activitiesRes?.data || [];
  const activitiesMeta = activitiesRes?.meta || { total: 0, page: 1, totalPages: 1 };

  // ── Mutations ──

  const updateContactMutation = useMutation({
    mutationFn: async (data: Partial<Contact>) => {
      await api.put(basePath, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contact', projectId, contactId] });
      queryClient.invalidateQueries({ queryKey: ['crm-contacts', projectId] });
      toast.success('Contact updated');
      setQuickInfoDirty(false);
    },
    onError: () => {
      toast.error('Failed to update contact');
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async (text: string) => {
      await api.post(`${basePath}/notes`, { text });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contact', projectId, contactId] });
      toast.success('Note added');
      setNewNoteText('');
    },
    onError: () => {
      toast.error('Failed to add note');
    },
  });

  const logActivityMutation = useMutation({
    mutationFn: async (data: { type: string; description: string }) => {
      await api.post(`${basePath}/activities`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-activities', projectId, contactId] });
      toast.success('Activity logged');
      setActivityDialogOpen(false);
      setActivityDescription('');
    },
    onError: () => {
      toast.error('Failed to log activity');
    },
  });

  // ── Handlers ──

  function handleSaveQuickInfo() {
    const payload: Partial<Contact> = {};
    if (editStatus) payload.status = editStatus as Contact['status'];
    if (editScore !== '') payload.score = Number(editScore);
    payload.assignedTo = editAssignedTo.trim() || undefined;
    payload.tags = editTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    updateContactMutation.mutate(payload);
  }

  function handleAddNote() {
    if (!newNoteText.trim()) {
      toast.error('Note text is required');
      return;
    }
    addNoteMutation.mutate(newNoteText.trim());
  }

  function handleLogActivity() {
    if (!activityDescription.trim()) {
      toast.error('Description is required');
      return;
    }
    logActivityMutation.mutate({
      type: activityType,
      description: activityDescription.trim(),
    });
  }

  function markQuickInfoDirty() {
    setQuickInfoDirty(true);
  }

  // ── Loading / Error ──

  if (contactLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        <span className="text-muted-foreground ml-2">Loading contact...</span>
      </div>
    );
  }

  if (contactError || !contact) {
    return (
      <div className="text-muted-foreground flex flex-col items-center justify-center py-24">
        <p className="font-medium">
          {contactError ? 'Failed to load contact' : 'Contact not found'}
        </p>
        <Link to={`/projects/${projectId}/crm`}>
          <Button variant="outline" size="sm" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to CRM
          </Button>
        </Link>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/projects/${projectId}/crm`}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{contact.name}</h1>
            <Badge variant="secondary" className={statusColor(contact.status)}>
              {contact.status}
            </Badge>
            <div className="flex items-center gap-1.5">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium">{contact.score}</span>
            </div>
          </div>
          <p className="text-muted-foreground text-sm">{contact.email}</p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column (2/3) */}
        <div className="space-y-6 lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-4 space-y-6">
              {/* Contact Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Contact Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex items-center gap-3">
                      <Mail className="text-muted-foreground h-4 w-4 shrink-0" />
                      <div>
                        <p className="text-muted-foreground text-xs">Email</p>
                        <p className="text-sm font-medium">{contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="text-muted-foreground h-4 w-4 shrink-0" />
                      <div>
                        <p className="text-muted-foreground text-xs">Phone</p>
                        <p className="text-sm font-medium">{contact.phone || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building className="text-muted-foreground h-4 w-4 shrink-0" />
                      <div>
                        <p className="text-muted-foreground text-xs">Company</p>
                        <p className="text-sm font-medium">{contact.company || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Activity className="text-muted-foreground h-4 w-4 shrink-0" />
                      <div>
                        <p className="text-muted-foreground text-xs">Source</p>
                        <p className="text-sm font-medium">
                          {contact.source
                            .replace('-', ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </p>
                      </div>
                    </div>
                    {(contact.tags || []).length > 0 && (
                      <div className="flex items-start gap-3 sm:col-span-2">
                        <Tag className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                          <p className="text-muted-foreground mb-1 text-xs">Tags</p>
                          <div className="flex flex-wrap gap-1">
                            {contact.tags.map((tag) => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Revenue Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-muted-foreground text-xs">Total Orders</p>
                      <p className="text-xl font-bold">{contact.totalOrders ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Total Revenue</p>
                      <p className="text-xl font-bold">{formatCents(contact.totalRevenue ?? 0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Custom Fields */}
              {contact.customFields && Object.keys(contact.customFields).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Custom Fields</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {Object.entries(contact.customFields).map(([key, value]) => (
                        <div key={key}>
                          <p className="text-muted-foreground text-xs">{key}</p>
                          <p className="text-sm font-medium">{String(value)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Activity Timeline</h3>
                <Button size="sm" onClick={() => setActivityDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Log Activity
                </Button>
              </div>

              {activitiesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                  <span className="text-muted-foreground ml-2 text-sm">Loading activities...</span>
                </div>
              ) : activities.length === 0 ? (
                <Card>
                  <CardContent className="text-muted-foreground flex flex-col items-center justify-center py-12">
                    <Activity className="mb-2 h-8 w-8" />
                    <p className="text-sm">No activities recorded yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-0">
                  {activities.map((act, idx) => (
                    <div key={act._id} className="flex gap-3">
                      {/* Timeline connector */}
                      <div className="flex flex-col items-center">
                        <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                          {activityIcon(act.type)}
                        </div>
                        {idx < activities.length - 1 && (
                          <div className="bg-border min-h-[24px] w-px flex-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {formatActivityType(act.type)}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {formatDateTime(act.createdAt)}
                          </span>
                        </div>
                        <p className="text-muted-foreground mt-0.5 text-sm">{act.description}</p>
                        {act.metadata && Object.keys(act.metadata).length > 0 && (
                          <div className="text-muted-foreground bg-muted mt-1 inline-block rounded px-2 py-1 text-xs">
                            {Object.entries(act.metadata).map(([k, v]) => (
                              <span key={k} className="mr-3">
                                <span className="font-medium">{k}:</span> {String(v)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Activity pagination */}
              {activitiesMeta.totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-muted-foreground text-xs">
                    Page {activitiesMeta.page} of {activitiesMeta.totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={activityPage <= 1}
                      onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={activityPage >= activitiesMeta.totalPages}
                      onClick={() => setActivityPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="mt-4 space-y-4">
              {/* Add Note Form */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Write a note..."
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      rows={3}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleAddNote}
                        disabled={addNoteMutation.isPending || !newNoteText.trim()}
                      >
                        {addNoteMutation.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        <Plus className="mr-2 h-4 w-4" />
                        Add Note
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes List */}
              {(contact.notes || []).length === 0 ? (
                <Card>
                  <CardContent className="text-muted-foreground flex flex-col items-center justify-center py-12">
                    <MessageSquare className="mb-2 h-8 w-8" />
                    <p className="text-sm">No notes yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {(contact.notes || []).map((note, idx) => (
                    <Card key={idx}>
                      <CardContent className="pb-3 pt-4">
                        <p className="whitespace-pre-wrap text-sm">{note.text}</p>
                        <Separator className="my-2" />
                        <div className="text-muted-foreground flex items-center gap-3 text-xs">
                          {note.createdBy && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {note.createdBy}
                            </span>
                          )}
                          {note.createdAt && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDateTime(note.createdAt)}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right column (1/3) — Quick Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">Status</Label>
                <Select
                  value={editStatus || contact.status}
                  onValueChange={(v) => {
                    setEditStatus(v as Contact['status']);
                    markQuickInfoDirty();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Score */}
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">Score</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={editScore}
                  onChange={(e) => {
                    setEditScore(Math.min(100, Math.max(0, Number(e.target.value) || 0)));
                    markQuickInfoDirty();
                  }}
                />
              </div>

              {/* Assigned To */}
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">Assigned To</Label>
                <Input
                  placeholder="Team member"
                  value={editAssignedTo}
                  onChange={(e) => {
                    setEditAssignedTo(e.target.value);
                    markQuickInfoDirty();
                  }}
                />
              </div>

              {/* Tags */}
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">Tags</Label>
                <Input
                  placeholder="vip, enterprise"
                  value={editTags}
                  onChange={(e) => {
                    setEditTags(e.target.value);
                    markQuickInfoDirty();
                  }}
                />
              </div>

              <Separator />

              {/* Read-only dates */}
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="text-muted-foreground h-3.5 w-3.5" />
                  <div>
                    <p className="text-muted-foreground text-xs">Last Contacted</p>
                    <p>{formatDate(contact.lastContactedAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Activity className="text-muted-foreground h-3.5 w-3.5" />
                  <div>
                    <p className="text-muted-foreground text-xs">Last Activity</p>
                    <p>{formatDate(contact.lastActivityAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="text-muted-foreground h-3.5 w-3.5" />
                  <div>
                    <p className="text-muted-foreground text-xs">Created</p>
                    <p>{formatDate(contact.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Save button */}
              <Button
                className="w-full"
                onClick={handleSaveQuickInfo}
                disabled={!quickInfoDirty || updateContactMutation.isPending}
              >
                {updateContactMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Log Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Activity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select
                value={activityType}
                onValueChange={(v) => setActivityType(v as ActivityType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {formatActivityType(t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the activity..."
                value={activityDescription}
                onChange={(e) => setActivityDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleLogActivity}
              disabled={logActivityMutation.isPending || !activityDescription.trim()}
            >
              {logActivityMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Log Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
